import { Box, Button, FormField, Input, Modal, SpaceBetween, Table } from '@cloudscape-design/components';
import { Dispatch, FC, useState } from 'react';
import { Loss } from '../api/agp';
import { createItem, deleteItem, Filter } from '../api/generic';
import { useAlertContext } from '../contexts/AlertContext';
import { useAuthContext } from '../contexts/AuthContext';
import useSubscribe from '../hooks/useSubscribe';
import { Batch } from '../models/Batch';
import { UserData } from '../models/UserData';
import { prefixCollection } from '../utils/prefix';
import { capitalizeFirstLetter } from '../utils/string';
import { toDate } from '../utils/timestamp';

export interface IModalProps {
  visible: boolean;
  setVisible: Dispatch<boolean>;
}

interface ILossModalProps extends IModalProps {
  batch: Batch;
}

const LossModal: FC<ILossModalProps> = ({ visible, setVisible, batch }) => {
  const alert = useAlertContext();
  const { tenant, userObject } = useAuthContext();
  const collection = prefixCollection('loss', tenant);
  const [thrubLoss, setThrubLoss] = useState<number>(0);
  const [filtrationLoss, setFiltrationLoss] = useState<number>(0);
  const [samplingLoss, setSamplingLoss] = useState<number>(0);

  const [losses, setLosses] = useState<Loss[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);

  const filters: Filter<Loss> = {
    field: 'batchId',
    operator: '==',
    value: batch.id,
  };
  const tenatFilters: Filter<UserData> = {
    field: 'tenant',
    operator: '==',
    value: tenant,
  };
  useSubscribe<Loss>(collection, setLosses, () => {}, undefined, [filters]);
  useSubscribe<UserData>('users', setUsers, () => {}, undefined, [tenatFilters]);

  const handleAddLoss = async (type: string) => {
    const item: Loss = {
      type,
      batchId: batch.id,
      date: new Date(),
      createdBy: userObject?.uid,
    };
    switch (type) {
      case 'thrub':
        await createItem<Loss>(collection, {
          ...item,
          amount: thrubLoss,
        });
        break;
      case 'filtration':
        await createItem<Loss>(collection, {
          ...item,
          amount: filtrationLoss,
        });
        break;
      case 'sampling':
        await createItem<Loss>(collection, {
          ...item,
          amount: samplingLoss,
        });
        break;

      default:
        break;
    }
    setThrubLoss(0);
    setFiltrationLoss(0);
    setSamplingLoss(0);
  };

  const handleDeleteLoss = async (id: string) => {
    if (!id) return;

    const confirmed = window.confirm('Are you sure you want to delete this loss entry?');
    if (!confirmed) return;

    await deleteItem(collection, id);
  };

  const handleAddTotalLoss = async () => {
    if (!batch.yield) {
      alert.warn('Batch yield not recorded. Cannot register total loss.');
      return;
    }

    // Check if total loss already registered
    if (losses.find((l) => l.type === 'total')) {
      alert.warn('Total loss already registered for this batch.');
      return;
    }

    const item: Loss = {
      type: 'total',
      batchId: batch.id,
      date: new Date(),
      createdBy: userObject?.uid,
      amount: batch.yield * 1000, // in liters
    };

    await createItem<Loss>(collection, item);
  };

  return (
    <Modal
      visible={visible}
      onDismiss={() => setVisible(false)}
      header={`Resgister loss for ${batch.name}`}
      size="large"
    >
      <SpaceBetween size="m" direction="vertical">
        <Box>
          Use this to register production losses for your batch. These losses help track yield discrepancies and are
          essential for accurate excise and inventory reporting.
        </Box>
        <FormField
          label="Thrub Loss (ml)"
          description="The volume of product left behind in tanks, hoses, or equipment after racking.

        "
        >
          <SpaceBetween size="xxs" direction="horizontal">
            <Input
              type="number"
              value={thrubLoss.toString()}
              onChange={({ detail }) => setThrubLoss(parseFloat(detail.value) || 0)}
            />
            <Button variant="icon" iconName="add-plus" onClick={() => handleAddLoss('thrub')} />
          </SpaceBetween>
        </FormField>

        <FormField
          label="Filtration Loss (ml)"
          description="Product volume lost during filtering (e.g. absorbed in filter pads).

"
        >
          <SpaceBetween size="xxs" direction="horizontal">
            <Input
              type="number"
              value={filtrationLoss.toString()}
              onChange={({ detail }) => setFiltrationLoss(parseFloat(detail.value) || 0)}
            />
            <Button variant="icon" iconName="add-plus" onClick={() => handleAddLoss('filtration')} />
          </SpaceBetween>
        </FormField>

        <FormField
          label="Sampling Loss (ml)"
          description="Any volume used for testing, quality control, or tasting.

"
        >
          <SpaceBetween size="xxs" direction="horizontal">
            <Input
              type="number"
              value={samplingLoss.toString()}
              onChange={({ detail }) => setSamplingLoss(parseFloat(detail.value) || 0)}
            />
            <Button variant="icon" iconName="add-plus" onClick={() => handleAddLoss('sampling')} />
          </SpaceBetween>
        </FormField>

        <Button onClick={() => handleAddTotalLoss()} iconName="status-negative">
          Register Total Batch Loss
        </Button>

        <Table
          items={losses}
          columnDefinitions={[
            {
              id: 'type',
              header: 'Type',
              cell: (x) => (x.type === 'total' ? 'Total batch loss' : capitalizeFirstLetter(x.type)),
            },
            {
              id: 'amount',
              header: 'Amount (mL)',
              cell: (x) => `${x.amount}mL`,
            },
            {
              id: 'date',
              header: 'Date',
              cell: (x) => toDate(x.date).toLocaleDateString(),
            },
            {
              id: 'createdBy',
              header: 'Created by',
              cell: (x) => {
                const u = users.find((u) => u.uid === x.createdBy);
                return u?.name;
              },
            },
            {
              id: 'actions',
              header: '',
              cell: (x) =>
                x.id ? <Button variant="icon" iconName="close" onClick={() => handleDeleteLoss(x.id ?? '')} /> : <></>,
            },
          ]}
          empty="Register loss to see items in this table"
        />
      </SpaceBetween>
    </Modal>
  );
};

export default LossModal;
